import type { NodeRef } from "../api/types";
import { T } from "../i18n";

// Contextual inspector for the selected node. Shows only customer-safe facts and
// offers drill-down into the explorer. No internal/draft fields are ever shown.

export interface InspectorProps {
  node: (NodeRef & { methods?: number }) | null;
  area?: string;
  uses?: number;
  usedBy?: number;
  onDrillDown: (id: string) => void;
  onViewSource: (id: string) => void;
  affectedActive: boolean;
  affectedCount?: number | null;
  onToggleAffected: () => void;
}

export function Inspector({ node, area, uses, usedBy, onDrillDown, onViewSource, affectedActive, affectedCount, onToggleAffected }: InspectorProps) {
  const sourceKnown = node?.hasSource !== undefined;
  const canView = !sourceKnown || !!node?.hasSource;
  return (
    <aside className="inspector" aria-label={T.map.inspector}>
      <h3>{T.map.inspector}</h3>
      {!node ? (
        <p className="muted">{T.map.selectHint}</p>
      ) : (
        <div className="inspector-body">
          <div className="inspector-title">
            <strong>{node.label}</strong>
            {area && <span className="inspector-area">{area}</span>}
          </div>
          {node.file && (
            <div className="inspector-file" title={node.file}>
              {node.file}
              {node.line ? <span className="inspector-line">:{node.line}</span> : null}
            </div>
          )}

          {(node.severity || node.findingStatus) && (
            <div className="inspector-row">
              {node.severity && <span className={`tag tag-${node.severity}`}>{node.severity} finding</span>}
              {node.findingStatus && <span className="tag">{node.findingStatus}</span>}
            </div>
          )}

          {(uses !== undefined || node.methods !== undefined) && (
            <dl className="inspector-facts">
              {uses !== undefined && (<><dt>Depends on</dt><dd>{uses}</dd></>)}
              {usedBy !== undefined && (<><dt>Used by</dt><dd>{usedBy}</dd></>)}
              {node.methods !== undefined && (<><dt>Methods</dt><dd>{node.methods}</dd></>)}
            </dl>
          )}

          <div className="inspector-actions">
            <button
              className="btn btn-accent"
              onClick={() => onViewSource(node.id)}
              disabled={!canView}
              aria-describedby={!canView ? "inspector-nosource" : undefined}
            >
              {T.source.viewSource}
            </button>
            {!canView && (
              <p id="inspector-nosource" className="inspector-note">
                Source for this type wasn't indexed in this snapshot.
              </p>
            )}
            <button className="btn" onClick={() => onDrillDown(node.id)}>
              {T.map.drillDown}
            </button>
            <button className="btn btn-ghost" onClick={onToggleAffected} aria-pressed={affectedActive}>
              {affectedActive ? T.map.affectedClear : T.map.affectedOn}
            </button>
            {affectedActive && affectedCount !== undefined && affectedCount !== null && (
              <p className="inspector-note">
                {affectedCount === 0
                  ? "Nothing else depends on this type."
                  : `${affectedCount} type${affectedCount === 1 ? "" : "s"} depend on it, directly or indirectly (dashed rings).`}
              </p>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
